import Konva from 'konva';
import {KonvaPointerEvent} from 'konva/types/PointerEvents';
import {compact} from 'lodash';
import React, {Fragment, useCallback, useRef, useState, useMemo, useEffect} from 'react';
import {useIntl} from 'react-intl';
import {Stage, Layer, Rect, Text, Line, Group} from 'react-konva';
import {
  BOX_LABEL_DEFAULT,
  BOX_LABEL_SELECTED,
  TEXT_COLOR,
  TEXT_SELECTED_COLOR,
} from '../../StudioAnnotator/const';
import {BoxLabelStyleProps} from '../../StudioAnnotator/types';
import {StudioTooltip} from '../../StudioTooltip';
import './StudioCanvasOverlay.scss';
import {Polygon} from '../../../components/DataPrepare/SegmentationStep/Annotator/Polygon';

export type BoundingBoxType = {
  id?: number;
  label?: string;
  bbox?: number[] | null | undefined;
  progress?: number;
  segmentation?: {x: number; y: number}[] | null | undefined;
};

export type StudioCanvasOverlayProps = {
  data: BoundingBoxType[];
  width: number;
  height: number;
  fontSize?: number;
  textColor?: string;
  boxColor?: string;
  onHover?: (index: number) => void;
  showAll?: boolean;
  showLabel?: boolean;
  selectOnMouseOver?: boolean;
  highlightIndex?: number | null;
  children?: React.ReactNode;
};

const LABEL_PADDING = 5;
const BORDER_SIZE = 1;

export const StudioCanvasOverlay = ({
  data,
  width = 100,
  height = 100,
  fontSize = 12,
  onHover = () => {},
  showAll = true,
  showLabel = false,
  selectOnMouseOver = false,
  highlightIndex = null,
  children,
}: StudioCanvasOverlayProps) => {
  const intl = useIntl();
  const overlayRef = useRef<HTMLDivElement>(null);
  const [selection, setSelection] = useState<number | null>(null);

  const handleMouseOver = useCallback(
    (e: KonvaPointerEvent, index: number) => {
      const target = e.currentTarget;
      target.moveToTop();
      setSelection(index);
      onHover(index);
    },
    [onHover]
  );

  const formatLabel = useCallback(
    (progress?: number, label?: string, maximumFractionDigits = 0) => {
      return compact([
        label,
        progress &&
          `(${intl.formatNumber(progress, {
            style: 'percent',
            maximumFractionDigits,
          })})`,
      ]).join(' ');
    },
    [intl]
  );

  const selectedAnnotation = selection !== null ? data?.[selection] : null;

  const {selectedX, selectedY, selectedWidth, selectedHeight} = useMemo(() => {
    if (selectedAnnotation?.bbox) {
      const [startX, startY, bboxWidth, bboxHeight] = selectedAnnotation.bbox;
      return {
        selectedX: startX,
        selectedY: startY,
        selectedWidth: bboxWidth,
        selectedHeight: bboxHeight,
      };
    }
    return {selectedX: 0, selectedY: 0, selectedWidth: 0, selectedHeight: 0};
  }, [selectedAnnotation]);

  const anchorEl = {
    clientWidth: selectedWidth,
    clientHeight: selectedHeight,
    getBoundingClientRect() {
      const offset = overlayRef.current?.getBoundingClientRect();
      if (!offset) return null;
      return {
        x: selectedX + offset.x,
        y: selectedY + offset.y,
        width: selectedWidth,
        height: selectedHeight,
        top: selectedY + offset.top,
        right: selectedX + selectedWidth + offset.left,
        bottom: selectedY + selectedHeight + offset.top,
        left: selectedX + offset.left,
      };
    },
  };

  const denormalizePoint = useCallback(
    (point: {x: number; y: number}) => ({
      x: point.x * width,
      y: point.y * height,
    }),
    [width, height]
  );

  const annotations = useMemo(
    () =>
      data.map((item, index) => {
        const isSelected =
          selectOnMouseOver && (index === selection || index === highlightIndex);
        const style: BoxLabelStyleProps = isSelected
          ? BOX_LABEL_SELECTED
          : BOX_LABEL_DEFAULT;

        if (
          item.segmentation?.length &&
          item.segmentation.every(point => point.x >= 0 && point.y >= 0)
        ) {
          const denormalizedPoints = item.segmentation.map(denormalizePoint);
          const polygonData: PolyLabelProps = {
            id: item.id || index,
            points: denormalizedPoints,
            label: item.label,
          };

          return (
            <Polygon
              key={`polygon-${polygonData.id}`}
              width={width}
              height={height}
              selected={isSelected}
              data={{...polygonData, id: `POLY_${polygonData.id}`}}
              onHover={() => handleMouseOver(null as any, index)}
            />
          );
        } else if (
          item.bbox &&
          (showAll || index === selection || index === highlightIndex)
        ) {
          const [startX, startY, bboxWidth, bboxHeight] = item.bbox;

          return (
            <Fragment key={index}>
              <Group
                x={startX}
                y={startY}
                onMouseOver={(e: KonvaPointerEvent) => handleMouseOver(e, index)}
                onMouseOut={() => setSelection(null)}
              >
                {showLabel && (
                  <AnnotationLabel
                    startX={startX}
                    startY={startY}
                    bboxWidth={bboxWidth}
                    imageWidth={width}
                    label={formatLabel(item.progress, item.label)}
                    selected={isSelected}
                    fontSize={fontSize}
                    fill={style.stroke}
                  />
                )}
                <Line
                  x={0}
                  y={0}
                  points={[
                    0,
                    0,
                    bboxWidth,
                    0,
                    bboxWidth,
                    bboxHeight,
                    0,
                    bboxHeight,
                    0,
                    0,
                  ]}
                  {...style}
                  strokeWidth={1}
                  hitStrokeWidth={20}
                  shadowForStrokeEnabled
                />
              </Group>
            </Fragment>
          );
        }
        return null;
      }),
    [
      data,
      width,
      height,
      showAll,
      fontSize,
      showLabel,
      selection,
      highlightIndex,
      selectOnMouseOver,
      formatLabel,
      handleMouseOver,
      denormalizePoint,
    ]
  );

  return (
    <div ref={overlayRef}>
      <StudioTooltip
        open={selection !== null}
        title={
          selection !== null &&
          formatLabel(data?.[selection]?.progress, data?.[selection]?.label, 1)
        }
        disableFocusListener
        disableHoverListener
        disableTouchListener
        PopperProps={{
          anchorEl,
          placement: 'bottom-start',
        }}
      >
        <div />
      </StudioTooltip>
      <Stage
        width={width}
        height={height}
        style={{width, height}}
        className="canvas-overlay-container"
      >
        <Layer>
          {children}
          {annotations}
        </Layer>
      </Stage>
    </div>
  );
};

export default StudioCanvasOverlay;

type AnnotationLabelProps = {
  startX: number;
  startY: number;
  bboxWidth: number;
  imageWidth: number;
  fontSize: number;
  selected: boolean;
  label: string;
  fill: string;
};

const AnnotationLabel = ({
  startX,
  startY,
  bboxWidth,
  imageWidth,
  fontSize,
  selected,
  label,
  fill,
}: AnnotationLabelProps) => {
  const textRef = useRef<Konva.Text>(null);
  const [textWidth, setTextWidth] = useState(0);

  const flagHeight = fontSize + LABEL_PADDING * 2;
  const flagWidth = Math.max(bboxWidth, textWidth) + 2 * BORDER_SIZE;
  const x1 = startX + flagWidth;

  // Calcular labelX correctamente
  const labelX = (x1 > imageWidth ? imageWidth - x1 : 0) - BORDER_SIZE;

  // Calcular labelY correctamente
  const labelY = startY < flagHeight ? 0 : -1 * flagHeight;

  useEffect(function adaptTextToLabel() {
    if (textRef.current) {
      const width = textRef.current.getTextWidth();

      setTextWidth(width + 2 * LABEL_PADDING);
    }
  }, []);

  return (
    <>
      <Rect
        opacity={0.75}
        x={labelX}
        y={labelY}
        width={flagWidth}
        height={flagHeight}
        fill={fill}
      />
      <Text
        ref={textRef}
        text={label}
        ellipsis
        x={labelX + LABEL_PADDING}
        y={labelY + LABEL_PADDING}
        height={flagHeight}
        fontSize={fontSize}
        fill={selected ? TEXT_SELECTED_COLOR : TEXT_COLOR}
      />
    </>
  );
};

type PolyLabelProps = {
  id: number;
  points: {x: number; y: number}[];
  label?: string;
};
